import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { RefreshCw, Upload, Trash2, Image, Clock, CheckCircle, AlertCircle, Car, Settings, GripVertical, ChevronLeft, ChevronRight, Database, Shield, HardDrive } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export default function AdminSync() {
  const [syncStatus, setSyncStatus] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [message, setMessage] = useState(null);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [backups, setBackups] = useState([]);
  const [backingUp, setBackingUp] = useState(false);
  const [healthStatus, setHealthStatus] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, placeholdersRes] = await Promise.all([
        axios.get(`${API}/sync/status`, { headers: getAuthHeader() }),
        axios.get(`${API}/sync/placeholders`, { headers: getAuthHeader() })
      ]);
      setSyncStatus(statusRes.data);
      setPlaceholders(placeholdersRes.data.images || []);
    } catch (err) {
      console.error('Error fetching sync data:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchBackups();
    fetchHealth();
  }, [fetchData]);

  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API}/admin/backups`, { headers: getAuthHeader() });
      setBackups(res.data.backups || []);
    } catch (err) {
      console.error('Error fetching backups:', err);
    }
  };

  const fetchHealth = async () => {
    try {
      const res = await axios.get(`${API}/health`);
      setHealthStatus(res.data);
    } catch (err) {
      setHealthStatus({ status: 'error', services: {} });
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    setMessage(null);
    try {
      await axios.post(`${API}/admin/backup`, {}, { headers: getAuthHeader() });
      setMessage({ type: 'success', text: 'Sauvegarde créée avec succès!' });
      fetchBackups();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setBackingUp(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    setMessage(null);
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/sync/upload-placeholder`, formData, {
          headers: getAuthHeader()
        });
        
        if (res.data.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        console.error('Upload error:', err);
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      setMessage({ type: 'success', text: `${successCount} photo(s) téléversée(s) avec succès!` });
      fetchData();
    }
    if (errorCount > 0 && successCount === 0) {
      setMessage({ type: 'error', text: `Erreur: ${errorCount} photo(s) ont échoué` });
    }
    
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (imageUrl) => {
    try {
      await axios.delete(`${API}/sync/placeholder?image_url=${encodeURIComponent(imageUrl)}`, {
        headers: getAuthHeader()
      });
      setMessage({ type: 'success', text: 'Photo supprimée' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Supprimer toutes les photos placeholder?')) return;
    try {
      await axios.post(`${API}/sync/clear-placeholders`, {}, { headers: getAuthHeader() });
      setMessage({ type: 'success', text: 'Toutes les photos supprimées' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setMessage(null);
    
    try {
      const res = await axios.post(`${API}/sync/trigger`, {}, { headers: getAuthHeader() });
      if (res.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Synchronisation terminée: ${res.data.added} ajoutés, ${res.data.skipped} ignorés` 
        });
        fetchData();
      } else {
        setMessage({ type: 'error', text: res.data.error || 'Erreur de synchronisation' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la synchronisation' });
    } finally {
      setSyncing(false);
    }
  };

  const handleApplyPlaceholders = async () => {
    setApplying(true);
    setMessage(null);
    
    try {
      const res = await axios.post(`${API}/sync/apply-placeholders`, {}, { headers: getAuthHeader() });
      if (res.data.success) {
        setMessage({ 
          type: 'success', 
          text: `Photos appliquées à ${res.data.updated} véhicules` 
        });
        fetchData();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'application' });
    } finally {
      setApplying(false);
    }
  };

  // Move image position
  const moveImage = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= placeholders.length) return;
    
    const newOrder = [...placeholders];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    
    // Optimistic update
    setPlaceholders(newOrder);
    
    try {
      await axios.post(`${API}/sync/reorder-placeholders`, { images: newOrder }, { headers: getAuthHeader() });
    } catch (err) {
      // Revert on error
      fetchData();
      setMessage({ type: 'error', text: 'Erreur lors du réordonnancement' });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, idx) => {
    setDraggedIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
  };

  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === dropIdx) {
      setDraggedIdx(null);
      return;
    }
    
    await moveImage(draggedIdx, dropIdx);
    setDraggedIdx(null);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-CA', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Synchronisation DealTrackerPro</h1>
          <p className="text-slate-500 text-sm mt-1">Gérer l'importation automatique des véhicules</p>
        </div>
        <Button 
          onClick={handleSync} 
          disabled={syncing}
          className="bg-[#1e3a5f] hover:bg-[#2d4a6f]"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Synchronisation...' : 'Synchroniser maintenant'}
        </Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Statut</p>
                <div className="mt-1">
                  {syncStatus?.enabled ? (
                    <Badge className="bg-green-100 text-green-700">Actif</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-700">Inactif</Badge>
                  )}
                </div>
              </div>
              <Settings className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Nouvelles arrivées</p>
                <p className="text-2xl font-bold text-slate-900">{syncStatus?.new_arrivals_count || 0}</p>
              </div>
              <Car className="h-8 w-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">À merchandiser</p>
                <p className="text-2xl font-bold text-orange-600">{syncStatus?.vehicles_needing_merchandising || 0}</p>
              </div>
              <Image className="h-8 w-8 text-orange-300" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Prochaine sync</p>
                <p className="text-sm font-bold text-slate-900">
                  {syncStatus?.next_scheduled_sync ? formatDate(syncStatus.next_scheduled_sync) : '-'}
                </p>
              </div>
              <Clock className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Last Sync Info */}
      {syncStatus?.last_result && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Dernière synchronisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div>
                <span className="text-slate-500">Date:</span>
                <span className="ml-2 font-medium">{formatDate(syncStatus.last_sync)}</span>
              </div>
              <div>
                <span className="text-slate-500">Ajoutés:</span>
                <span className="ml-2 font-medium text-green-600">{syncStatus.last_result.added}</span>
              </div>
              <div>
                <span className="text-slate-500">Ignorés:</span>
                <span className="ml-2 font-medium text-slate-600">{syncStatus.last_result.skipped}</span>
              </div>
              <div>
                <span className="text-slate-500">Total reçu:</span>
                <span className="ml-2 font-medium">{syncStatus.last_result.total_received}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Placeholder Images */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Image className="h-4 w-4" />
              Photos placeholder pour nouvelles arrivées ({placeholders.length})
            </CardTitle>
            <div className="flex gap-2">
              {placeholders.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Tout supprimer
                </Button>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button asChild variant="default" size="sm" disabled={uploading}>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'Téléversement...' : 'Ajouter des photos'}
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            Glissez-déposez pour réorganiser. La première photo sera l'image principale.
          </p>
          
          {placeholders.length === 0 ? (
            <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
              <Image className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="font-medium">Aucune photo placeholder</p>
              <p className="text-sm">Téléversez des photos pour les nouvelles arrivées</p>
              <label className="cursor-pointer mt-4 inline-block">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button asChild variant="outline" size="sm">
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    Sélectionner des photos
                  </span>
                </Button>
              </label>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {placeholders.map((url, idx) => (
                <div 
                  key={url + idx}
                  draggable
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={(e) => handleDrop(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`relative group cursor-move ${
                    draggedIdx === idx ? 'opacity-50 scale-95' : ''
                  } transition-all`}
                >
                  {/* Drag handle */}
                  <div className="absolute top-1 left-1 z-10 p-1 bg-black/50 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical className="h-4 w-4 text-white" />
                  </div>
                  
                  {/* Image */}
                  <img
                    src={getImageUrl(url)}
                    alt={`Placeholder ${idx + 1}`}
                    className="w-full h-28 object-cover rounded-lg border bg-slate-100"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="12">Erreur</text></svg>';
                    }}
                  />
                  
                  {/* Delete button */}
                  <button
                    onClick={() => handleDelete(url)}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                  
                  {/* Position badge */}
                  <Badge className={`absolute bottom-1 left-1 text-[10px] px-1.5 ${
                    idx === 0 ? 'bg-blue-600 text-white' : 'bg-black/60 text-white'
                  }`}>
                    {idx === 0 ? 'Principal' : `#${idx + 1}`}
                  </Badge>
                  
                  {/* Arrow controls */}
                  <div className="absolute bottom-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {idx > 0 && (
                      <button
                        onClick={() => moveImage(idx, idx - 1)}
                        className="p-0.5 bg-white/90 rounded shadow hover:bg-white"
                      >
                        <ChevronLeft className="h-4 w-4 text-slate-700" />
                      </button>
                    )}
                    {idx < placeholders.length - 1 && (
                      <button
                        onClick={() => moveImage(idx, idx + 1)}
                        className="p-0.5 bg-white/90 rounded shadow hover:bg-white"
                      >
                        <ChevronRight className="h-4 w-4 text-slate-700" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Database Health & Backups */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Santé du système & Sauvegardes
            </CardTitle>
            <Button
              onClick={handleBackup}
              disabled={backingUp}
              size="sm"
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              <Database className={`h-4 w-4 mr-2 ${backingUp ? 'animate-spin' : ''}`} />
              {backingUp ? 'Sauvegarde...' : 'Sauvegarder maintenant'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Health Status */}
          {healthStatus && (
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-2">
                <div className={`h-2.5 w-2.5 rounded-full ${healthStatus.status === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm font-medium">
                  Système: {healthStatus.status === 'ok' ? 'En ligne' : 'Problème détecté'}
                </span>
              </div>
              {healthStatus.services?.database && (
                <div className="flex items-center gap-2">
                  <div className={`h-2.5 w-2.5 rounded-full ${healthStatus.services.database.status === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-sm">Base de données</span>
                </div>
              )}
              {healthStatus.services?.scheduler && (
                <div className="flex items-center gap-2">
                  <div className={`h-2.5 w-2.5 rounded-full ${healthStatus.services.scheduler.status === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-sm">Planificateur ({healthStatus.services.scheduler.jobs} tâches)</span>
                </div>
              )}
              {healthStatus.services?.inventory && (
                <div className="flex items-center gap-2">
                  <HardDrive className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-sm">{healthStatus.services.inventory.vehicle_count} véhicules en base</span>
                </div>
              )}
            </div>
          )}

          {/* Backups List */}
          {backups.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 mb-2">Sauvegardes automatiques chaque nuit à 3h. Les 7 dernières sont conservées.</p>
              <div className="divide-y">
                {backups.map((b, idx) => (
                  <div key={b.name} className="flex items-center justify-between py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4 text-slate-400" />
                      <span className="font-mono text-xs">{b.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-slate-500">{b.size_mb} MB</span>
                      {idx === 0 && <Badge className="bg-green-100 text-green-700 text-[10px]">Dernière</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">Aucune sauvegarde disponible</p>
          )}
        </CardContent>
      </Card>

      {/* Uptime Monitoring */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Surveillance de disponibilité (Recommandé)
          </h3>
          <p className="text-sm text-amber-800 mb-3">
            Pour être alerté immédiatement si votre site tombe, configurez un service de surveillance gratuit:
          </p>
          <ol className="text-sm text-amber-800 space-y-1 list-decimal list-inside">
            <li>Créez un compte gratuit sur <a href="https://uptimerobot.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">UptimeRobot.com</a></li>
            <li>Ajoutez un nouveau moniteur HTTP(s)</li>
            <li>URL à surveiller: <code className="bg-amber-100 px-1 rounded text-xs">{process.env.REACT_APP_BACKEND_URL}/api/health</code></li>
            <li>Intervalle: 5 minutes</li>
            <li>Configurez les alertes par email/SMS</li>
          </ol>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-blue-900 mb-2">Comment ça fonctionne</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Téléversez vos photos placeholder ci-dessus (plusieurs à la fois)</li>
            <li>• <strong>Glissez-déposez</strong> ou utilisez les flèches pour réorganiser l'ordre</li>
            <li>• La première photo sera l'image principale affichée dans la grille</li>
            <li>• <strong>Automatique:</strong> Les nouveaux véhicules reçoivent ces photos dès leur importation</li>
            <li>• Les véhicules déjà sur le site ne sont jamais modifiés</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
